import { useEffect, useMemo, useState } from 'react';
import { Download, ExternalLink, Search, X } from 'lucide-react';
import type { ProductLineRow } from '@contracts';
import { ODOO_WEB_URL } from '@/lib/config';
import { canOpenOdooLinks } from '@/services/authService';
import { exportExcel } from '@/lib/exportData';

const money = (n: number) =>
  n.toLocaleString('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 2 });
const num = (n: number) => Math.round(n).toLocaleString('es-MX');
const fmtDay = (iso?: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' });
};
const orderUrl = (id: number) => `${ODOO_WEB_URL}/web#id=${id}&model=pos.order&view_type=form`;
const isRealLine = (l: ProductLineRow) => !l.isRefund && !l.wasRefunded && !l.isZero;

type SortCol = 'product' | 'qty' | 'pos' | 'customer' | 'amount';
type SortDir = 'asc' | 'desc';
const PAGE_OPTS = [10, 50, 100, 0];

export interface ProductsDrilldownModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  lines: ProductLineRow[];
  loading?: boolean;
}

/** Drill-down de PIEZAS por orden (KPI Productos vendidos): muestra todas las líneas
 *  marcando devueltas/refunds/$0; el cálculo (netas) las excluye. */
export function ProductsDrilldownModal({ open, onClose, title, lines, loading }: ProductsDrilldownModalProps) {
  const [q, setQ] = useState('');
  const [sortCol, setSortCol] = useState<SortCol>('qty');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(0);

  useEffect(() => setPage(0), [q, sortCol, sortDir, pageSize, lines]);

  const head = useMemo(() => {
    const real = lines.filter(isRealLine);
    return {
      netUnits: real.reduce((s, l) => s + l.qty, 0),
      orders: new Set(lines.map((l) => l.orderId)).size,
      revenue: real.reduce((s, l) => s + l.amount, 0),
    };
  }, [lines]);

  const toggleSort = (col: SortCol) => {
    if (col === sortCol) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortCol(col);
      setSortDir('desc');
    }
  };

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    let rows = lines;
    if (term) {
      rows = rows.filter((l) =>
        [l.product, l.orderRef, l.customer, l.posName].some((f) => f?.toLowerCase().includes(term)),
      );
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    const str = (a: string, b: string) => a.localeCompare(b) * dir;
    const sorted = [...rows];
    sorted.sort((a, b) => {
      switch (sortCol) {
        case 'qty': return (a.qty - b.qty) * dir;
        case 'amount': return (a.amount - b.amount) * dir;
        case 'pos': return str(a.posName, b.posName);
        case 'customer': return str(a.customer, b.customer);
        default: return str(a.product, b.product);
      }
    });
    return sorted;
  }, [lines, q, sortCol, sortDir]);

  const netUnitsF = useMemo(() => filtered.filter(isRealLine).reduce((s, l) => s + l.qty, 0), [filtered]);
  const grossRows = useMemo(() => filtered.filter((l) => !l.isRefund && !l.isZero), [filtered]);
  const grossUnitsF = useMemo(() => grossRows.reduce((s, l) => s + l.qty, 0), [grossRows]);
  const pages = pageSize ? Math.max(1, Math.ceil(filtered.length / pageSize)) : 1;
  const start = pageSize ? page * pageSize : 0;
  const rows = pageSize ? filtered.slice(start, start + pageSize) : filtered;

  const doExport = () => {
    const estado = (l: ProductLineRow) =>
      l.isRefund ? 'Devolución' : l.wasRefunded ? 'Devuelta' : l.isZero ? 'Garantía/$0' : 'Venta';
    void exportExcel(
      'productos',
      filtered.map((l) => ({
        Producto: l.product,
        Piezas: l.qty,
        Orden: l.orderRef,
        Sucursal: l.posName,
        Cliente: l.customer,
        Fecha: l.date,
        Importe: l.amount,
        Estado: estado(l),
      })),
    );
  };

  if (!open) return null;

  const arrow = (col: SortCol) => (sortCol === col ? (sortDir === 'asc' ? '↑' : '↓') : '');
  const cols: { col: SortCol; label: string; right: boolean }[] = [
    { col: 'product', label: 'Producto', right: false },
    { col: 'qty', label: 'Piezas', right: true },
    { col: 'pos', label: 'Sucursal · Folio', right: false },
    { col: 'customer', label: 'Cliente', right: false },
    { col: 'amount', label: 'Importe', right: true },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="flex max-h-[90dvh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center gap-4 bg-gradient-to-r from-emerald-700 via-emerald-600 to-teal-600 px-5 py-4 text-white">
          <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl border border-white/25 bg-white/15 text-2xl">📦</div>
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-xl font-black leading-tight">{title}</h2>
            <p className="mt-0.5 truncate text-sm text-white/80">
              <span className="font-semibold text-white">{num(head.netUnits)} piezas</span> netas
              {' · '}{head.orders} órdenes{' · '}<span className="font-semibold text-white">{money(head.revenue)}</span>
            </p>
          </div>
          <button onClick={onClose} title="Cerrar" className="shrink-0 rounded-lg p-1.5 text-white/80 hover:bg-white/15">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-5 py-3">
          <div className="relative min-w-[200px] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar producto, folio, cliente, sucursal…"
              className="w-full rounded-xl border border-slate-300 py-2 pl-9 pr-3 text-sm outline-none focus:border-emerald-400"
            />
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Mostrar</span>
            <div className="flex gap-1">
              {PAGE_OPTS.map((p) => (
                <button
                  key={p}
                  onClick={() => setPageSize(p)}
                  className={`rounded-lg px-2.5 py-1.5 text-xs font-semibold ${
                    pageSize === p ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {p === 0 ? 'Todos' : p}
                </button>
              ))}
            </div>
          </div>
          <button
            onClick={doExport}
            title="Descargar Excel"
            className="flex items-center gap-1.5 rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-600 hover:border-emerald-400 hover:text-emerald-600"
          >
            <Download className="h-4 w-4" /> Excel
          </button>
        </div>

        {/* Tabla */}
        <div className="min-h-0 flex-1 overflow-auto">
          {loading ? (
            <div className="p-10 text-center text-sm text-slate-400">Cargando productos…</div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-400">Sin productos para los filtros actuales.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500 shadow-sm">
                <tr>
                  {cols.map((h) => (
                    <th
                      key={h.col}
                      onClick={() => toggleSort(h.col)}
                      className={`cursor-pointer select-none px-4 py-2.5 font-semibold hover:text-emerald-600 ${h.right ? 'text-right' : ''}`}
                    >
                      {h.label}
                      <span className="ml-0.5 inline-block w-2 text-emerald-600">{arrow(h.col)}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((l) => {
                  const accent = l.wasRefunded
                    ? 'border-l-rose-400 bg-rose-50/60'
                    : l.isRefund
                      ? 'border-l-amber-400 bg-amber-50/60'
                      : l.isZero
                        ? 'border-l-slate-300 bg-slate-50'
                        : 'border-l-transparent hover:bg-slate-50';
                  const tag = l.wasRefunded
                    ? { label: `Devuelta ${fmtDay(l.refundedAt)}`, cls: 'bg-rose-100 text-rose-700' }
                    : l.isRefund
                      ? { label: `Devolución${l.refundOfDate ? ` · de ${fmtDay(l.refundOfDate)}` : ''}`, cls: 'bg-amber-100 text-amber-700' }
                      : l.isZero
                        ? { label: 'Garantía / $0', cls: 'bg-slate-200 text-slate-600' }
                        : null;
                  return (
                    <tr key={l.id} className={`border-l-4 border-b border-slate-100 ${accent}`}>
                      <td className="px-4 py-2.5">
                        <span className="font-medium text-slate-700">{l.product}</span>
                        {tag && (
                          <span className={`ml-1 inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold ${tag.cls}`}>{tag.label}</span>
                        )}
                      </td>
                      <td className={`px-4 py-2.5 text-right font-bold tabular-nums ${l.qty < 0 ? 'text-rose-600' : 'text-slate-700'}`}>{num(l.qty)}</td>
                      <td className="px-4 py-2.5">
                        <p className="text-slate-700">{l.posName}</p>
                        {canOpenOdooLinks() ? (
                          <a
                            href={orderUrl(l.orderId)}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Abrir en Odoo"
                            className="group inline-flex items-center gap-1 font-mono text-[11px] font-semibold text-emerald-700 hover:underline"
                          >
                            {l.orderRef}
                            <ExternalLink className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" />
                          </a>
                        ) : (
                          <span className="font-mono text-[11px] font-semibold text-slate-700">{l.orderRef}</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5">{l.customer}</td>
                      <td
                        className={`px-4 py-2.5 text-right font-bold tabular-nums ${
                          l.isRefund ? 'text-rose-600' : l.wasRefunded ? 'text-slate-400 line-through' : 'text-emerald-700'
                        }`}
                      >
                        {money(l.amount)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer: Piezas brutas − Devueltas = Netas */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-slate-100 bg-slate-50/60 px-5 py-3 text-sm">
          <span className="text-slate-500">
            Piezas brutas ({grossRows.length}):{' '}
            <span className="font-semibold text-slate-700">{num(grossUnitsF)}</span>
          </span>
          {grossUnitsF - netUnitsF > 0 && (
            <span className="text-rose-500">
              − Devueltas: <span className="font-semibold">{num(grossUnitsF - netUnitsF)}</span>
            </span>
          )}
          <span className="text-slate-600">
            = Piezas netas: <span className="font-bold text-emerald-700">{num(netUnitsF)}</span>
          </span>
          <div className="ml-auto flex items-center gap-3 text-slate-500">
            <span className="text-xs">
              {pageSize ? `${start + 1}–${Math.min(start + pageSize, filtered.length)} de ${filtered.length}` : `${filtered.length} líneas`}
            </span>
            {pageSize > 0 && pages > 1 && (
              <div className="flex items-center gap-2">
                <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} className="rounded-lg px-2 py-1 hover:bg-slate-200 disabled:opacity-40">‹</button>
                <span className="text-xs">{page + 1} / {pages}</span>
                <button onClick={() => setPage((p) => Math.min(pages - 1, p + 1))} disabled={page >= pages - 1} className="rounded-lg px-2 py-1 hover:bg-slate-200 disabled:opacity-40">›</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
